import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { faCircleCheck, faEnvelope } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { T, useT } from "@repo/transifex";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useOrderEmail } from "~/hooks/queries/orders";
import {
  Button,
  Dialog,
  DialogError,
  DialogLoading,
  DialogSuccess
} from "~/components";
import { basketActions } from "~/providers/store/basket";
import { employeeActions } from "~/providers/store/employee";
import { useCountdown } from "~/hooks/use-countdown";
import { DEFAULT_COUNTDOWN_SECONDS, EMAIL_REGEX } from "~/utils/constants";
import { useUserProductsPos } from "~/api/products/hooks/use-user-products";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";
import { cn } from "~/lib/utils";

type FormData = { email: string };

export const PosEmailReceiptPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>();

  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("orderId");

  const { data: userProducts } = useUserProductsPos();
  const { mutate: sendEmail, status: emailStatus, reset } = useOrderEmail();

  const { data: till } = useCurrentTill();
  const isAttended = hasAttendedSellConfig(till);

  const handleClose = () => {
    navigate(isAttended ? "/pos/sell" : "/pos");

    basketActions.reset();
    employeeActions.reset();

    if (
      till?.type === "self-service" ||
      (till?.type === "attended" && till.config.resetDiningOption)
    ) {
      basketActions.resetDiningOption();
    }
  };

  // Use useCountdown for UI countdown display (only when needed)
  const { timeLeftInSeconds, resetCountdown } = useCountdown({
    countdownDurationInSeconds: DEFAULT_COUNTDOWN_SECONDS,
    onCountdownFinished: handleClose,
    disabled: isAttended || emailStatus === "pending"
  });

  if (emailStatus === "pending") {
    return (
      <DialogLoading
        backdropVariant="success"
        size="md"
        title={
          <span className="font-semibold">
            <T _str="Please wait" />
          </span>
        }
      >
        <T _str="The email is being sent..." />
      </DialogLoading>
    );
  }

  if (emailStatus === "error" || !orderId) {
    return (
      <DialogError
        backdropVariant="success"
        error={
          <span className="font-semibold">
            <T _str="Something went wrong" />
          </span>
        }
        hideSupport
        size="md"
      >
        <p className="text-center">
          <T _str="We couldn't sent a receipt to the email." />
        </p>

        <div className="flex w-full flex-col gap-y-5 pt-5">
          <Button
            className="w-full"
            onClick={() => {
              reset();
              resetCountdown();
            }}
            variant="primary"
          >
            <T _str="Try again" />
          </Button>
          <Button className="w-full" onClick={handleClose} variant="secondary">
            {isAttended ? (
              <T _str="Close" />
            ) : (
              <T _str="Close ({seconds})" seconds={timeLeftInSeconds} />
            )}
          </Button>
        </div>
      </DialogError>
    );
  }

  if (emailStatus === "success") {
    return (
      <DialogSuccess backdropVariant="success" size="md">
        <p className="text-center text-xl font-semibold text-primary-main">
          <T _str="Email has been sent" />
        </p>

        <div className="w-full pt-5">
          <Button className="w-full" onClick={handleClose} variant="primary">
            {isAttended ? (
              <T _str="Close" />
            ) : (
              <T _str="Close ({seconds})" seconds={timeLeftInSeconds} />
            )}
          </Button>
        </div>
      </DialogSuccess>
    );
  }

  const isEmailInvalid = Boolean(errors.email);

  return (
    <Dialog backdropVariant="success" size="md">
      <form
        className="flex flex-col gap-y-5"
        onSubmit={handleSubmit(({ email }: FormData) => {
          sendEmail({
            orderId,
            email,
            userId: userProducts?.user?.id ?? null
          });
          resetCountdown();
        })}
        noValidate
      >
        <div className="flex flex-col gap-y-5 px-5 pt-8">
          <div className="flex items-center justify-center gap-x-5 text-primary-main">
            <span className="text-3xl font-semibold">
              <T _str="Payment completed" />
            </span>

            <FontAwesomeIcon className="h-11" icon={faCircleCheck} />
          </div>

          <div className="flex flex-col items-center justify-center gap-y-5">
            <p className="text-lg font-semibold text-interface-neutral-6">
              <T _str="Send receipt on email" />
            </p>

            <div
              className={cn(
                "relative flex w-full flex-col rounded-md border border-gray-300 px-4 py-5 text-left",
                {
                  "border-interface-negative-2": isEmailInvalid
                }
              )}
            >
              <input
                className="border-none text-base text-interface-neutral-6 outline-none"
                placeholder={t("Type email")}
                type="email"
                {...register("email", {
                  required: t("Email is required"),
                  pattern: {
                    value: EMAIL_REGEX,
                    message: t("Invalid email address")
                  }
                })}
                onInput={() => {
                  resetCountdown();
                }}
              />

              <FontAwesomeIcon
                className="absolute right-4 top-1/3"
                icon={faEnvelope}
              />
            </div>

            {errors.email ? (
              <span className="text-sm font-semibold text-interface-negative-2">
                {errors.email.message}
              </span>
            ) : null}
          </div>
        </div>

        <div className="px-5 pb-5">
          <div className="flex gap-x-2">
            <Button
              className="w-2/6 text-lg"
              onClick={handleClose}
              size="large"
              type="button"
              variant="secondary"
            >
              <T _str="Close" />
            </Button>

            <Button
              disabled={isEmailInvalid}
              className="w-2/3 text-lg"
              size="large"
              type="submit"
              variant="primary"
            >
              <T _str="Send receipt" />
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};
